import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { useUser } from '@/state/session/context'
import { sessionQueryKeys } from '@/lib/query/query-keys'

export interface ProTrial {
  status: 'unavailable' | 'active' | 'expired'
  source: 'initial_quests' | null
  startsAt: string | null
  endsAt: string | null
  daysRemaining: number
  isPremium: boolean
  freeHabitLimit: number
}

export function trialDaysRemaining(trial: ProTrial, now = Date.now()): number {
  return trial.endsAt ? Math.max(0, Math.ceil((Date.parse(trial.endsAt) - now) / 86_400_000)) : 0
}

export function useProTrial() {
  const user = useUser()
  const client = useQueryClient()
  const previous = useRef<string>()
  const key = ['billing', 'trial', user?.id] as const
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.get<ProTrial>('/billing/trial'),
    enabled: Boolean(user?.id),
    staleTime: 30_000,
    refetchInterval: (state) => {
      const end = state.state.data?.endsAt
      const remaining = end ? Date.parse(end) - Date.now() : Infinity
      return remaining > 0 ? Math.min(60_000, remaining) : 60_000
    },
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    const refresh = () => { void client.invalidateQueries({ queryKey: ['billing', 'trial', user?.id] }) }
    window.addEventListener('habitpack:missions-updated', refresh)
    window.addEventListener('habitpack:mission-claimed', refresh)
    return () => {
      window.removeEventListener('habitpack:missions-updated', refresh)
      window.removeEventListener('habitpack:mission-claimed', refresh)
    }
  }, [client, user?.id])

  useEffect(() => {
    if (!query.data) return
    const next = `${user?.id}:${query.data.status}:${query.data.isPremium}:${query.data.daysRemaining}`
    if (previous.current === next) return
    previous.current = next
    void client.invalidateQueries({ queryKey: sessionQueryKeys.profile(user?.id) })
    void client.invalidateQueries({ queryKey: ['subscription', 'status'] })
    void client.invalidateQueries({ queryKey: ['dashboard'] })
    void client.invalidateQueries({ queryKey: ['manage'] })
    void client.invalidateQueries({ queryKey: ['habits'] })
    window.dispatchEvent(new CustomEvent('habitpack:profile-updated'))
    window.dispatchEvent(new CustomEvent('habitpack:habits-updated'))
    window.dispatchEvent(new CustomEvent('habitpack:notifications-updated'))
  }, [query.data, client, user?.id])

  return query
}
